/**
 * @fileoverview The KPI workbook's layout and the text form of every cell —
 * one definition shared by the download (write) and the upload (read), so the
 * two can never disagree about a column.
 *
 * A catalogue is `{ modules, units, databases }`, each `{ value, label }[]`.
 */

import {
  AGGREGATION_METHODS,
  CALCULATION_METHODS,
  EF_BASES,
  EMISSION_SCOPES,
  FACTOR_GASES,
  EVIDENCE_KINDS,
  FACTOR_COUNTRIES,
  FIXED_TAG_GROUPS,
  FREQUENCIES,
  KPI_CATEGORIES,
  KPI_STATUSES,
  KPI_TYPES,
  QUESTION_TYPES,
  TAG_FIELDS,
} from "../options";

export const SHEET = {
  kpis: "KPIs",
  factors: "Emission factors",
  lists: "Lists",
  help: "Read me",
};

export const YES_NO = ["Yes", "No"];

export const KPI_COLUMNS = [
  { key: "id", header: "KPI ID", width: 10, readOnly: true },
  { key: "title", header: "KPI title", width: 48 },
  { key: "module", header: "Module", width: 26, list: "modules" },
  { key: "type", header: "Type", width: 20, list: "types" },
  { key: "kpiType", header: "KPI type", width: 22 },
  { key: "frequency", header: "Frequency", width: 14, list: "frequencies" },
  { key: "aggregation", header: "Aggregation", width: 13, list: "aggregations" },
  { key: "category", header: "Category", width: 14, list: "categories" },
  { key: "defaultDescription", header: "Default description", width: 40 },
  { key: "yearDescription", header: "This year description", width: 40 },
  { key: "readOnly", header: "Read only", width: 11, list: "yesNo" },
  { key: "units", header: "Supported units", width: 30 },
  ...FIXED_TAG_GROUPS.flatMap((group) =>
    TAG_FIELDS.map((field, index) => ({
      key: `tag:${group.name}:${index}`,
      header: `${group.label} · ${field.label}`,
      width: 18,
      tagGroup: group.name,
      tagIndex: index,
    })),
  ),
  { key: "evidences", header: "Evidence", width: 30 },
  { key: "industries", header: "Industries", width: 30 },
  { key: "frameworks", header: "Frameworks", width: 30 },
  { key: "sectionMapping", header: "Framework section mapping", width: 30 },
  { key: "formula", header: "Formula", width: 30 },
  { key: "variables", header: "Variables", width: 22 },
  { key: "applicableCheck", header: "Applicable check", width: 16, list: "yesNo" },
  { key: "applicableNote", header: "Applicable note", width: 16, list: "yesNo" },
  { key: "trigerCheck", header: "Trigger check", width: 14, list: "yesNo" },
  { key: "kpiStatus", header: "KPI status", width: 14, list: "kpiStatuses" },
  { key: "active", header: "Active", width: 9, list: "yesNo" },
];

export const FACTOR_COLUMNS = [
  { key: "factorId", header: "Factor ID", width: 10, readOnly: true },
  { key: "kpiId", header: "KPI ID", width: 10 },
  { key: "kpiTitle", header: "KPI title", width: 44 },
  { key: "scope", header: "Scope", width: 10, list: "scopes" },
  { key: "country", header: "Country", width: 10, list: "countries", open: true },
  { key: "unit", header: "Per unit", width: 14, list: "units" },
  { key: "value", header: "Value", width: 14 },
  { key: "source", header: "Source", width: 26, list: "databases" },
  { key: "method", header: "Method", width: 26, list: "methods", breakdown: true },
  { key: "basis", header: "Gas values are", width: 18, list: "bases", breakdown: true },
  { key: "density", header: "Density (kg per unit)", width: 14, breakdown: true },
  { key: "ncv", header: "NCV (TJ/Gg)", width: 12, breakdown: true },
  ...FACTOR_GASES.map(({ gas, ef }) => ({ key: ef, header: `${gas.toUpperCase()} factor`, width: 12, breakdown: true })),
  ...FACTOR_GASES.map(({ gas, gwp }) => ({ key: gwp, header: `${gas.toUpperCase()} GWP`, width: 10, breakdown: true })),
  { key: "gwpSource", header: "GWP source", width: 14, breakdown: true },
  { key: "delete", header: "Delete", width: 9, list: "yesNo" },
];

/**
 * The dropdown lists, in Lists-sheet column order.
 * @param {{ modules: object[], units: object[], databases: object[] }} catalogue
 * @returns {{ name: string, header: string, values: string[] }[]}
 */
export const listsFor = (catalogue) => [
  { name: "modules", header: "Modules", values: catalogue.modules.map((o) => o.label) },
  { name: "types", header: "Types", values: QUESTION_TYPES.map((o) => o.label) },
  { name: "kpiTypes", header: "KPI types", values: KPI_TYPES.map((o) => o.label) },
  { name: "frequencies", header: "Frequencies", values: FREQUENCIES.map((o) => o.label) },
  { name: "aggregations", header: "Aggregations", values: AGGREGATION_METHODS.map((o) => o.label) },
  { name: "categories", header: "Categories", values: KPI_CATEGORIES.map((o) => o.label) },
  { name: "units", header: "Units", values: uniqueLabels(catalogue.units) },
  { name: "databases", header: "Sources", values: catalogue.databases.map((o) => o.label) },
  { name: "scopes", header: "Scopes", values: EMISSION_SCOPES.map((o) => o.label) },
  { name: "methods", header: "Methods", values: CALCULATION_METHODS.map((o) => o.label) },
  { name: "bases", header: "Gas value bases", values: EF_BASES.map((o) => o.label) },
  { name: "countries", header: "Countries", values: FACTOR_COUNTRIES },
  { name: "industries", header: "Industries", values: (catalogue.industries || []).map((o) => o.label) },
  { name: "frameworks", header: "Frameworks", values: (catalogue.frameworks || []).map((o) => o.label) },
  { name: "kpiStatuses", header: "KPI statuses", values: KPI_STATUSES.map((o) => o.label) },
  { name: "evidence", header: "Evidence kinds", values: EVIDENCE_KINDS },
  { name: "yesNo", header: "Yes / No", values: YES_NO },
];

const uniqueLabels = (options) => [...new Set(options.map((o) => o.label))];

const norm = (text) => String(text ?? "").trim().toLowerCase();

const squash = (text) => norm(text).replace(/\s+/g, "");

/**
 * Finds an option by its label or its stored value, ignoring case and spaces, so a download from before a label
 * lost a space ("Already kg CO₂e" → "Already kgCO₂e") still uploads.
 * @param {{ value: string, label: string }[]} options
 * @param {string} text
 * @returns {string|null} the option's value
 */
export const optionValue = (options, text) => {
  const needle = squash(text);
  if (!needle) return null;
  const hit = options.find((o) => squash(o.label) === needle || squash(o.value) === needle);
  return hit ? hit.value : null;
};

/** @param {{ value: string, label: string }[]} options */
export const optionLabel = (options, value) =>
  options.find((o) => String(o.value) === String(value))?.label ?? "";

/**
 * Unit names are not unique in riu_unit_master, so a name can stand for
 * several ids. The one the KPI already uses wins; otherwise the first.
 *
 * @param {{ value: string, label: string }[]} units
 * @param {string} text
 * @param {(number|string)[]} [preferIds]
 * @returns {number|null}
 */
export const unitIdFor = (units, text, preferIds = []) => {
  const needle = norm(text);
  if (!needle) return null;
  const hits = units.filter((u) => norm(u.label) === needle || norm(u.value) === needle);
  if (!hits.length) return null;
  const preferred = hits.find((u) => preferIds.map(String).includes(String(u.value)));
  return Number((preferred || hits[0]).value);
};

/**
 * `kWh (base); MWh = 1000` — the base unit is marked, every other unit
 * carries its factor into the base.
 * @param {{ unitId: number, factor: number, isBase: boolean }[]} units
 * @param {{ value: string, label: string }[]} unitOptions
 */
export const formatUnits = (units, unitOptions) =>
  (units || [])
    .map((unit) => {
      const name = optionLabel(unitOptions, unit.unitId) || `unit ${unit.unitId}`;
      return unit.isBase ? `${name} (base)` : `${name} = ${unit.factor}`;
    })
    .join("; ");

const UNIT_PART = /^(.*?)\s*(\(base\))?\s*(?:=\s*(\S+))?$/i;

/**
 * Reads the Supported units text back. A single unit is the base whether
 * marked or not; with several, exactly one must say `(base)`.
 *
 * @param {string} text
 * @param {{ value: string, label: string }[]} unitOptions
 * @param {(number|string)[]} [preferIds]
 * @returns {{ units: { unitId: number, factor: number, isBase: boolean }[], error: string|null }}
 */
export const parseUnits = (text, unitOptions, preferIds = []) => {
  const parts = String(text ?? "")
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean);
  const units = [];
  for (const part of parts) {
    const [, name, baseMark, factorText] = part.match(UNIT_PART);
    const unitId = unitIdFor(unitOptions, name, preferIds);
    if (unitId == null) return { units: [], error: `Unknown unit "${name}"` };
    const isBase = !!baseMark;
    const factor = isBase ? 1 : Number(factorText ?? 1);
    if (!Number.isFinite(factor)) return { units: [], error: `"${part}" needs a number after =` };
    units.push({ unitId, factor, isBase });
  }
  if (units.length === 1) units[0] = { ...units[0], isBase: true, factor: 1 };
  if (units.length > 1 && !units.some((u) => u.isBase)) {
    return { units: [], error: "Mark the base unit with (base)" };
  }
  return { units, error: null };
};

/** @param {string[]} list */
export const formatList = (list) => (list || []).join("; ");

/**
 * @param {string} text `a; b; c`
 * @param {string[]} allowed
 * @returns {{ values: string[], error: string|null }}
 */
export const parseList = (text, allowed) => {
  const values = [];
  const parts = String(text ?? "")
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean);
  for (const part of parts) {
    const hit = allowed.find((a) => norm(a) === norm(part));
    if (!hit) return { values: [], error: `Unknown value "${part}"` };
    if (!values.includes(hit)) values.push(hit);
  }
  return { values, error: null };
};

/**
 * `Invoice; STP Log book` — several values in one cell where any text is
 * allowed. A value matching a known one in another case takes its spelling.
 * @param {string} text
 * @param {string[]} known
 * @returns {string[]}
 */
export const parseFreeList = (text, known) => {
  const values = [];
  String(text ?? "")
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((part) => {
      const value = known.find((k) => norm(k) === norm(part)) ?? part;
      if (!values.some((v) => norm(v) === norm(value))) values.push(value);
    });
  return values;
};

/**
 * `Energy (Sector); Waste` — several options in one cell, by label.
 * @param {string} text
 * @param {{ value: string, label: string }[]} options
 * @returns {{ values: string[], error: string|null }} option values
 */
export const parseOptionList = (text, options) => {
  const values = [];
  const parts = String(text ?? "")
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean);
  for (const part of parts) {
    const value = optionValue(options, part);
    if (value == null) return { values: [], error: `Unknown value "${part}"` };
    if (!values.includes(value)) values.push(value);
  }
  return { values, error: null };
};

/**
 * @param {string} text
 * @returns {boolean|null} null when blank, undefined-like when not yes/no
 */
export const parseYesNo = (text) => {
  const value = norm(text);
  if (!value) return null;
  if (["yes", "y", "true", "1"].includes(value)) return true;
  if (["no", "n", "false", "0"].includes(value)) return false;
  return undefined;
};
